/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useOrganizationId } from 'hooks/useOrganizationId';
import { fetchOrganizationCampaignThunk } from 'actions/emailMarketing/EmailMarketingActions';
import { toEmailCampaign } from '../apiAdapters';
import { EmailCampaign } from '../types';

const SENDING_POLL_MS = 10000;

interface CampaignDetail {
    campaign: EmailCampaign | undefined;
    loading: boolean;
}

// Campaign for the report page, with totals and per-location counts.
export const useCampaignDetail = (campaignId: number): CampaignDetail => {
    const dispatch = useDispatch();
    const organizationId = useOrganizationId();
    const campaign = useSelector((state: RootState) => state.emailMarketing.campaign);
    // Tracked here so an error from another request doesn't end this page's loading state.
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!organizationId || !campaignId) return;
        setFailed(false);
        (dispatch(fetchOrganizationCampaignThunk(organizationId, campaignId) as any) as Promise<unknown>).catch(() =>
            setFailed(true)
        );
    }, [dispatch, organizationId, campaignId]);

    // While it's sending, refresh so the status and results update without a reload.
    const isSending = campaign?.id === campaignId && campaign.status === 'sending';
    useEffect(() => {
        if (!isSending || !organizationId) return;
        const timer = setInterval(
            () => dispatch(fetchOrganizationCampaignThunk(organizationId, campaignId) as any),
            SENDING_POLL_MS
        );
        return () => clearInterval(timer);
    }, [dispatch, isSending, organizationId, campaignId]);

    // Ignore a previously opened campaign while this one loads.
    const current = campaign?.id === campaignId ? campaign : undefined;
    const apiCampaign = useMemo(() => (current ? toEmailCampaign(current) : undefined), [current]);

    // Loading until the detail arrives; a failed request shows "not found".
    return { campaign: apiCampaign, loading: !apiCampaign && !failed && !!campaignId };
};

export default useCampaignDetail;
